/**
 * Гутлын хүснэгт — ЦОРЫН ГАНЦ хэлбэр.
 *
 * Хоёр дуудагч: «Бүх гутал» (`/items`) ба «Агуулах»
 * (`/warehouse`). Багана тодорхойлолт хоёр хуудсанд давхардаж эхэлсэн
 * тул энд гаргав — шинэ багана нэмэх бол ЭНД нэмнэ.
 *
 * ⚠️ Серийн дугаар нь `formatSerialDisplay`-ээр — шошго ба дэлгэц
 *    ИЖИЛ хэлбэртэй байх ёстой (CLAUDE.md, серийн дугаарын хэсэг).
 */

import { formatDate, formatLocation, formatSerialDisplay, formatSize } from '@boots/domain';
import type { ReactNode } from 'react';
import type { ItemListRow } from '../lib/types';
import { DataTable, type Column } from './data-table';
import { Money, StatusBadge } from './ui';

export function ItemTable({
  rows, selectedId = null, onPick, showCost, empty,
}: {
  rows: ItemListRow[];
  /** Сонгосон мөрийг тодруулна — дэлгэрэнгүй панелтай хуудсанд */
  selectedId?: string | null;
  onPick?: (id: string) => void;
  showCost: boolean;
  empty?: ReactNode;
}) {
  return (
    <DataTable
      rows={rows}
      columns={itemColumns(showCost, selectedId)}
      rowKey={(it) => it.id}
      onRowClick={onPick ? (it) => onPick(it.id) : undefined}
      empty={empty}
    />
  );
}

function itemColumns(showCost: boolean, selectedId: string | null): Column<ItemListRow>[] {
  const cost: Column<ItemListRow>[] = [
    {
      key: 'cost',
      header: 'Өртөг',
      align: 'right',
      cell: (it) => <span className="text-ink-2"><Money value={it.landedCostMnt} /></span>,
    },
  ];

  return [
    {
      key: 'serial',
      header: 'Серийн дугаар',
      mobile: 'title',
      nowrap: true,
      cell: (it) => (
        <span className={`num ${selectedId === it.id ? 'font-semibold' : ''}`}>
          {it.serial ? (
            formatSerialDisplay(it.serial)
          ) : (
            <span className="rounded-full bg-alert/10 px-[11px] py-1 text-xs text-alert-ink">
              шошгогүй
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'model',
      header: 'Загвар',
      // Кирилл загварын нэр — тайрахгүй, боож өгнө
      cell: (it) => (
        <span className="flex flex-col">
          <span className="font-medium text-pretty">{it.model.name}</span>
          <span className="text-xs text-ink-2">{it.model.brand}</span>
        </span>
      ),
    },
    { key: 'size', header: 'Размер', align: 'right', cell: (it) => formatSize(it.size) },
    {
      key: 'pay',
      header: 'Төлбөр',
      // Зээлтэйг Л тэмдэглэнэ — ихэнх гутал бэлнээр авагддаг тул мөр
      // бүрд «бэлэн» гэж бичих нь баганыг чимээ болгоно.
      cell: (it) =>
        it.isCredit ? (
          <span className="rounded-full bg-alert-bg px-2 py-0.5 text-xs font-medium text-alert-ink">
            Зээл
          </span>
        ) : (
          <span className="text-ink-2">—</span>
        ),
    },
    { key: 'status', header: 'Төлөв', cell: (it) => <StatusBadge status={it.status} /> },
    {
      key: 'location',
      header: 'Байршил',
      cell: (it) => <span className="text-ink-2 text-pretty">{formatLocation(it.location)}</span>,
    },
    {
      key: 'supplier',
      header: 'Нийлүүлэгч',
      mobile: 'hide',
      cell: (it) => <span className="text-ink-2 text-pretty">{it.supplier.name}</span>,
    },
    {
      key: 'price',
      header: 'Зарах үнэ',
      align: 'right',
      mobile: 'value',
      cell: (it) => <Money value={it.priceMnt} />,
    },
    ...(showCost ? cost : []),
    {
      key: 'collected',
      header: 'Орсон',
      align: 'right',
      cell: (it) => <span className="text-ink-2">{formatDate(it.collectedAt)}</span>,
    },
  ];
}
